"use client";

import React, { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { CheckCircle, Mail } from "lucide-react";
import HomePageLayout from "@/components/layout/HomePageLayout";
import { useToast } from "@/components/ui/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  reportDownloadFormSchema,
  type ReportDownloadFormData,
} from "@/lib/validations/report-download-form";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";

const ReportPage = () => {
  const { toast } = useToast();
  const [showSuccess, setShowSuccess] = useState(false);
  const successRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showSuccess) successRef.current?.focus();
  }, [showSuccess]);
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const turnstileRef = useRef<TurnstileInstance>(null);
  const [isDevelopment, setIsDevelopment] = useState(true);

  useEffect(() => {
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    const noSiteKey =
      !process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ||
      process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY === "";
    setIsDevelopment(isLocalhost || noSiteKey);
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<ReportDownloadFormData>({
    resolver: zodResolver(reportDownloadFormSchema),
    defaultValues: {
      marketingConsent: false as unknown as true,
    },
  });

  const marketingConsent = watch("marketingConsent");

  const onSubmit = async (data: ReportDownloadFormData) => {
    try {
      const response = await fetch("/api/reports/download", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-screen-width": window.screen.width.toString(),
          "x-screen-height": window.screen.height.toString(),
        },
        body: JSON.stringify({ ...data, turnstileToken }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Submission failed");
      }

      setShowSuccess(true);
    } catch (error) {
      if (!isDevelopment && turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setTurnstileToken("");

      toast({
        title: "Something went wrong",
        description:
          error instanceof Error
            ? error.message
            : "Please try again or email us directly.",
        variant: "destructive",
      });
    }
  };

  return (
    <HomePageLayout>
    <section
      className="relative bg-[#070203] min-h-screen mt-2 overflow-x-hidden"
      style={{
        backgroundImage: "url(/radial_pattern.png)",
        backgroundPosition: "center 250px",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
      }}
    >
      <div className="relative z-10">
        <div className="container px-3 sm:px-4 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
            {/* Mobile: Title (order 1) / Desktop: hidden (title is inside form column) */}
            <div className="lg:sr-only order-1 text-white text-center">
              <h1 className="font-prociono text-4xl sm:text-5xl lg:text-6xl">
                The State of Design<br />Talent in Europe
              </h1>
            </div>

            {/* Report Image - order 2 on mobile, order 2 on desktop */}
            <div className="flex flex-col items-center order-2 lg:order-2 lg:col-span-7 -mx-6 sm:-mx-8 lg:mx-0">
              <div className="relative w-full">
                <Image
                  src="/Mockup_-_Main.png"
                  alt="The State of Design Talent in Europe 2025 - Open Book Preview"
                  width={1200}
                  height={848}
                  className="w-full h-auto drop-shadow-2xl"
                  priority
                />
              </div>
            </div>

            {/* Mobile: Description (order 3) / Desktop: hidden (description is inside form column) */}
            <div className="lg:hidden order-3 text-white text-center">
              <p className="text-[#d9cbcc] text-sm sm:text-base">
                97,022 product designers across 15 countries. Salaries, experience gaps and what they mean if you&apos;re hiring.
              </p>
            </div>

            {/* Form column - order 4 on mobile, order 1 on desktop */}
            <div className="relative order-4 lg:order-1 lg:col-span-5">
              <div className="pt-4 pb-8 lg:pt-12 lg:pb-12 text-white">
                {showSuccess ? (
                  <div ref={successRef} role="status" tabIndex={-1} className="text-center py-8 focus:outline-none">
                    <div className="mb-6 flex justify-center">
                      <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                        <CheckCircle className="w-10 h-10 text-green-500" />
                      </div>
                    </div>
                    <h3 className="font-prociono text-2xl sm:text-3xl mb-4">
                      Check your email!
                    </h3>
                    <p className="text-[#d9cbcc] text-base mb-4">
                      We&apos;ve sent the download link to your inbox.
                    </p>
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1a1012] border border-[#2f1b1d] text-sm text-[#d9cbcc]">
                      <Mail className="w-4 h-4" />
                      <span>The link is valid for 7 days</span>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Desktop only: title + description */}
                    <div className="mb-6 hidden lg:block">
                      <p aria-hidden="true" className="font-prociono text-4xl sm:text-5xl lg:text-6xl mb-3">
                        The State of Design Talent in Europe
                      </p>
                      <p className="text-[#d9cbcc] text-sm sm:text-base">
                        97,022 product designers across 15 countries. Salaries, experience gaps and what they mean if you&apos;re hiring.
                      </p>
                    </div>

                    <form
                      noValidate onSubmit={handleSubmit(onSubmit)}
                      className="space-y-5"
                    >
                      <div className="space-y-1">
                        <label htmlFor="report-page-name" className="text-base text-[#d9cbcc]">
                          Name <span className="text-red-400">*</span>
                        </label>
                        <input
                          {...register("name")}
                          id="report-page-name"
                          autoComplete="name"
                          aria-required="true"
                          aria-invalid={!!errors.name}
                          aria-describedby={errors.name ? "report-page-name-error" : undefined}
                          className="w-full rounded-lg backdrop-blur-sm border border-[#3C2227] px-4 py-3.5 text-base text-white placeholder:text-[#7f6d70] focus:border-[#f98f2a] focus:ring-1 focus:ring-[#f98f2a] outline-none"
                          style={{ backgroundColor: "rgba(24, 14, 17, 0.4)" }}
                          placeholder="Your name"
                        />
                        {errors.name && (
                          <p id="report-page-name-error" role="alert" className="text-xs text-red-400">
                            {errors.name.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <label htmlFor="report-page-email" className="text-base text-[#d9cbcc]">
                          Email <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="email"
                          {...register("email")}
                          id="report-page-email"
                          autoComplete="email"
                          aria-required="true"
                          aria-invalid={!!errors.email}
                          aria-describedby={errors.email ? "report-page-email-error" : undefined}
                          className="w-full rounded-lg backdrop-blur-sm border border-[#3C2227] px-4 py-3.5 text-base text-white placeholder:text-[#7f6d70] focus:border-[#f98f2a] focus:ring-1 focus:ring-[#f98f2a] outline-none"
                          style={{ backgroundColor: "rgba(24, 14, 17, 0.4)" }}
                          placeholder="you@example.com"
                        />
                        {errors.email && (
                          <p id="report-page-email-error" role="alert" className="text-xs text-red-400">
                            {errors.email.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <label htmlFor="report-page-linkedinUrl" className="text-base text-[#d9cbcc]">
                          LinkedIn Profile URL{" "}
                          <span className="text-red-400">*</span>
                        </label>
                        <input
                          {...register("linkedinUrl")}
                          id="report-page-linkedinUrl"
                          autoComplete="url"
                          aria-required="true"
                          aria-invalid={!!errors.linkedinUrl}
                          aria-describedby={errors.linkedinUrl ? "report-page-linkedinUrl-error" : undefined}
                          className="w-full rounded-lg backdrop-blur-sm border border-[#3C2227] px-4 py-3.5 text-base text-white placeholder:text-[#7f6d70] focus:border-[#f98f2a] focus:ring-1 focus:ring-[#f98f2a] outline-none"
                          style={{ backgroundColor: "rgba(24, 14, 17, 0.4)" }}
                          placeholder="https://linkedin.com/in/yourprofile"
                        />
                        {errors.linkedinUrl && (
                          <p id="report-page-linkedinUrl-error" role="alert" className="text-xs text-red-400">
                            {errors.linkedinUrl.message}
                          </p>
                        )}
                      </div>

                      {/* Consent checkbox */}
                      <div className="space-y-3 pt-2">
                        <label className="flex items-start gap-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            aria-required="true"
                            aria-invalid={!!errors.marketingConsent}
                            aria-describedby={errors.marketingConsent ? "report-page-consent-error" : undefined}
                            checked={marketingConsent === true}
                            onChange={(e) =>
                              setValue(
                                "marketingConsent",
                                e.target.checked as unknown as true,
                                { shouldValidate: true }
                              )
                            }
                            className="mt-0.5 w-4 h-4 rounded border-[#3C2227] bg-[#1a1012] text-[#F97316] focus:ring-[#F97316] focus:ring-offset-0"
                          />
                          <span className="text-sm text-[#d9cbcc] group-hover:text-white transition-colors">
                            I agree to receive the report and future design
                            industry updates.{" "}
                            <span className="text-red-400">*</span>
                          </span>
                        </label>
                        {errors.marketingConsent && (
                          <p id="report-page-consent-error" role="alert" className="text-xs text-red-400 ml-7">
                            {errors.marketingConsent.message}
                          </p>
                        )}
                      </div>

                      {/* Turnstile Bot Protection */}
                      {!isDevelopment && (
                        <div className="flex pt-2">
                          <Turnstile
                            ref={turnstileRef}
                            siteKey={
                              process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ""
                            }
                            onSuccess={(token) => setTurnstileToken(token)}
                            onError={() => setTurnstileToken("")}
                            onExpire={() => setTurnstileToken("")}
                            options={{
                              theme: "dark",
                              size: "normal",
                            }}
                          />
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={
                          isSubmitting ||
                          (!isDevelopment && !turnstileToken) ||
                          !marketingConsent
                        }
                        className="w-full rounded-lg bg-[#F97316] hover:bg-[#ea6c0f] text-white font-semibold py-3.5 text-lg transition disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? "Sending..." : "Send me"}
                      </button>

                      <p className="text-xs text-center lg:text-left text-[#7f6d70]">
                        Your data is safe with us. Read our{" "}
                        <a
                          href="/privacy"
                          className="text-[#F97316] hover:underline"
                        >
                          Privacy Policy
                        </a>
                        .
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
    </HomePageLayout>
  );
};

export default ReportPage;
